import {
  type Company,
  type CompanyBranch,
  setIsCompaniesRequested,
  setCompanies,
  type queryKboResponse,
} from '../../../Store/DataSlice'
import './CompanyEdit.scss'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { TextInput } from '../../../Components/TextInput/TextInput'
import { useLocalization } from '../../../Hooks/useLocalization'
import { Button } from '../../../Components/Button/Button'
import { useDispatch, useSelector } from 'react-redux'
import { setDestinationView } from '../../../Store/InterfaceSlice'
import { NumberInput } from '../../../Components/NumberInput/NumberInput'
import { type RootState } from '../../../Store/Store'
import {
  getSimplifiedVatNumber,
  isBelgianVat,
  isEmpty,
  isValidEmail,
  isValidWebsite,
} from '../../../Utils/Validations'
import { getAuthorizationHeader } from '../../../Orchestration/Authentication'
import {
  formatCompanyBranch,
  getKboUrl,
  toBelgianDate,
} from '../../../Utils/Formatting'
import { CountryCodes, websitePlaceholder } from '../../../Utils/Constants'
// import { getNaceCodeText } from '../../../Utils/NaceCodes'
import { SingleSelect } from '../../../Components/SingleSelect/SingleSelect'
import uuid4 from 'uuid4'
import { LoadingSpinner } from '../../../Components/LoadingSpinner/LoadingSpinner'

let latestFetchId: string | undefined
const accidentMostRecentYear = new Date().getFullYear() - 1

const emptyBranchToAdd: CompanyBranch = {
  name: undefined,
  addressLine1: '',
  city: '',
  number: '',
  postalCode: '',
  countryCode: 'BE',
}

interface CompanyAddProps {
  vatNumber: string
}
export function CompanyEdit({ vatNumber }: CompanyAddProps) {
  const dispatch = useDispatch()
  const localization = useLocalization('company')
  const countryLocalization = useLocalization('country')

  const isCompaniesRequested = useSelector(
    (state: RootState) => state.data.isCompaniesRequested
  )
  useEffect(() => {
    if (!isCompaniesRequested) dispatch(setIsCompaniesRequested(true))
  }, [isCompaniesRequested, dispatch])

  const allCompanies = useSelector((state: RootState) => state.data.companies)
  const baseCompany = allCompanies.find((c) => c.vatNumber === vatNumber)

  const [companyChange, setCompanyChange] = useState<Partial<Company>>({})
  const company = useMemo<Company | undefined>(
    () =>
      baseCompany === undefined
        ? undefined
        : { ...baseCompany, ...companyChange },
    [baseCompany, companyChange]
  )

  const [kboLookupFailed, setKboLookupFailed] = useState(false)
  const [isFetchDataInProgress, setIsFetchDataInProgress] = useState(false)
  const reloadVatInfo = useCallback(() => {
    if (company?.vatNumber == null) return
    const simplifiedVatNumber = getSimplifiedVatNumber(company.vatNumber)
    const countryCode = simplifiedVatNumber.substring(0, 2).toUpperCase()
    if (countryCode === 'BE') {
      setIsFetchDataInProgress(true)
      setKboLookupFailed(false)
      const fetchId = uuid4()
      latestFetchId = fetchId
      fetch(
        `https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/query-kruispuntbank?vatNumber=${simplifiedVatNumber}`,
        { headers: { ...getAuthorizationHeader() } }
      )
        .then(async (response) => await response.json())
        .then((response: queryKboResponse) => {
          if (fetchId === latestFetchId) {
            if (response.success) {
              setCompanyChange((p) => ({
                ...p,
                ...response.lookupData,
                lastKpbCheck: Date.now(),
                lastKpbUpdate: Date.now(),
                lastKpbCheckSuccess: true,
              }))
            } else {
              setKboLookupFailed(true)
              setCompanyChange((p) => ({
                ...p,
                lastKpbCheck: Date.now(),
                lastKpbCheckSuccess: false,
              }))
              console.warn(
                `KBO lookup failed for ${simplifiedVatNumber}`,
                response.error
              )
              alert(
                `KBO lookup failed for ${simplifiedVatNumber}: ${response.error}`
              )
            }
            setIsFetchDataInProgress(false)
          } else {
            console.log(`Dropping outdated fetch result: `, response)
          }
        })
        .catch((err) => {
          console.warn(`fetch query-kruispuntbank failed: `, err)
        })
    } else {
      setIsFetchDataInProgress(false)
    }
  }, [company?.vatNumber])

  const isBelgianVatNumber = useMemo(
    () => isBelgianVat(company?.vatNumber) && !kboLookupFailed,
    [company, kboLookupFailed]
  )

  const [branchToAdd, setBranchToAdd] = useState<CompanyBranch>({
    ...emptyBranchToAdd,
    countryCode: company?.countryCode ?? '',
  })
  const [accidentCountYearToAdd, setAccidentCountYearToAdd] = useState(
    accidentMostRecentYear - 1
  )

  const [validationErrors, setValidationErrors] = useState<validationError[]>(
    []
  )
  useEffect(() => {
    const newValidationErrors: validationError[] = []
    if (!company) return
    if (!isBelgianVatNumber) {
      if (isEmpty(company?.name))
        newValidationErrors.push({ property: 'name', type: 'isRequired' })

      if (isEmpty(company?.legalForm))
        newValidationErrors.push({ property: 'legalForm', type: 'isRequired' })

      if (isEmpty(company?.countryCode))
        newValidationErrors.push({
          property: 'countryCode',
          type: 'isRequired',
        })
    }
    if (company.website && !isValidWebsite(company?.website))
      newValidationErrors.push({ property: 'website', type: 'format' })
    if (company.email && !isValidEmail(company.email))
      newValidationErrors.push({ property: 'email', type: 'format' })
    setValidationErrors(newValidationErrors)
  }, [company, isBelgianVatNumber])

  const [branchValidationErrors, setBranchValidationErrors] = useState<
    validationError[]
  >([])
  useEffect(() => {
    const newBranchValidationErrors: validationError[] = []
    if ((branchToAdd.number?.length ?? 0) === 0)
      newBranchValidationErrors.push({
        property: 'branch-number',
        type: 'isRequired',
      })
    if (isEmpty(branchToAdd.countryCode))
      newBranchValidationErrors.push({
        property: 'branch-countryCode',
        type: 'isRequired',
      })
    setBranchValidationErrors(newBranchValidationErrors)
  }, [branchToAdd])

  const [saveInProgress, setSaveInProgress] = useState(false)
  const saveCompany = () => {
    if (company == null) return
    setSaveInProgress(true)

    fetch(
      `https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/company-add`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthorizationHeader(),
        },
        body: JSON.stringify({
          company,
        }),
      }
    )
      .then(() => {
        dispatch(
          setCompanies([
            ...allCompanies.filter((c) => c.vatNumber !== company.vatNumber),
            company,
          ])
        )
        setTimeout(() => {
          dispatch(setDestinationView({ destinationView: 'company-list' }))
        }, 1)
      })
      .catch((err) => {
        console.warn(`fetch company-add failed: `, err)
      })
  }

  if (!company) return null

  return (
    <div className="company-edit">
      {saveInProgress ? (
        <div className="save-in-progress">
          <LoadingSpinner color="dark" />
          {localization.text(
            'company-save-in-progress',
            'De onderneming word momenteel opgeslagen.'
          )}
        </div>
      ) : isFetchDataInProgress ? (
        <div className="vat-fetch-in-progress">
          <LoadingSpinner color="dark" />
          {localization.text(
            'vat-info-fetch-in-progress',
            'Gegevens voor onderneming {vatNumber} worden opgehaald',
            { vatNumber: company.vatNumber }
          )}
        </div>
      ) : !baseCompany ? (
        <div className="loading">
          <LoadingSpinner color="dark" />
          {localization.text(
            'company-is-loading',
            'De onderneming word momenteel ingeladen.'
          )}
        </div>
      ) : (
        <>
          <div className="display-field">
            <div className="display-label">
              {localization.text('vat-number', 'Ondernemingsnummer')}
            </div>
            <div className="display-value">
              {company.vatNumber}
              {isBelgianVatNumber ? (
                <Button
                  className="reload-vat-info-button"
                  importance={'secondary'}
                  size="small"
                  text={localization.text(
                    'reload-vat-info',
                    'Herlaad onderneming informatie'
                  )}
                  onClick={reloadVatInfo}
                />
              ) : null}
            </div>
          </div>
          <TextInput
            label={localization.text('name', 'Naam')}
            isReadOnly={isBelgianVatNumber || saveInProgress}
            onChange={(newValue) => {
              setCompanyChange((previous) => ({
                ...previous,
                name: newValue,
              }))
            }}
            value={company.name}
            isValid={!validationErrors.some((ve) => ve.property === 'name')}
            helperText={
              validationErrors.find((ve) => ve.property === 'name')?.type ===
              'isRequired'
                ? localization.text('name-missing', 'Geef een geldige naam in')
                : undefined
            }
          />
          <TextInput
            label={localization.text('legal-form', 'Rechtsvorm')}
            isReadOnly={isBelgianVatNumber || saveInProgress}
            onChange={(newValue) => {
              setCompanyChange((previous) => ({
                ...previous,
                legalForm: newValue,
              }))
            }}
            value={company.legalForm}
            isValid={
              !validationErrors.some((ve) => ve.property === 'legalForm')
            }
            helperText={
              validationErrors.find((ve) => ve.property === 'legalForm')
                ?.type === 'isRequired'
                ? localization.text(
                    'legalForm-missing',
                    'Geef een rechtsvorm in'
                  )
                : undefined
            }
          />
          <TextInput
            label={localization.text('email', 'Email')}
            isReadOnly={saveInProgress}
            onChange={(newValue) => {
              setCompanyChange((previous) => ({
                ...previous,
                email: newValue,
              }))
            }}
            value={company.email}
            isValid={!validationErrors.some((ve) => ve.property === 'email')}
            helperText={
              validationErrors.find((ve) => ve.property === 'email')?.type ===
              'format'
                ? localization.text(
                    'email-invalid',
                    'Email formaat is incorrect'
                  )
                : undefined
            }
          />
          <TextInput
            label={localization.text('website', 'Website')}
            isReadOnly={saveInProgress}
            onChange={(newValue) => {
              setCompanyChange((previous) => ({
                ...previous,
                website: newValue,
              }))
            }}
            value={company.website}
            placeholder={websitePlaceholder}
            isValid={!validationErrors.some((ve) => ve.property === 'website')}
            helperText={
              validationErrors.find((ve) => ve.property === 'website')?.type ===
              'format'
                ? localization.text(
                    'website-invalid',
                    'Website formaat is incorrect'
                  )
                : undefined
            }
          />
          <TextInput
            label={localization.text('addressLine1', 'Straat en huisnummer')}
            isReadOnly={isBelgianVatNumber || saveInProgress}
            onChange={(newValue) => {
              setCompanyChange((previous) => ({
                ...previous,
                addressLine1: newValue,
              }))
            }}
            value={company.addressLine1}
          />
          <TextInput
            label={localization.text('postalCode', 'Postcode')}
            isReadOnly={isBelgianVatNumber || saveInProgress}
            onChange={(newValue) => {
              setCompanyChange((previous) => ({
                ...previous,
                postalCode: newValue,
              }))
            }}
            value={company.postalCode}
          />
          <TextInput
            label={localization.text('city', 'Gemeente')}
            isReadOnly={isBelgianVatNumber || saveInProgress}
            onChange={(newValue) => {
              setCompanyChange((previous) => ({
                ...previous,
                city: newValue,
              }))
            }}
            value={company.city}
          />
          <div className="display-field">
            <div className="display-label">
              {localization.text('country', 'Land')}
            </div>
            <div className="display-value">
              {countryLocalization.text(
                company.countryCode,
                company.countryCode
              )}
            </div>
          </div>
          {/* <div className="display-field">
            <div className="display-label">
              {localization.text('nace-code', 'NACE Code')}
            </div>
            <div className="display-value">
              {company.naceCode}: {getNaceCodeText(company.naceCode)}
            </div>
          </div> */}

          <div className="accident-counts">
            <div className="label">
              {localization.text(
                'accident-counts',
                'Arbeidsongevallen cijfers'
              )}
            </div>
            {(company.accidentCounts ?? []).map((ac) => (
              <div key={ac.year} className="accident-count">
                <div className="accident-count-year">{ac.year}</div>
                <NumberInput
                  value={ac.count}
                  min={0}
                  max={99999.99}
                  precision={2}
                  onChange={(newValue) => {
                    setCompanyChange((previous) => ({
                      ...previous,
                      accidentCounts: (
                        previous.accidentCounts ??
                        company.accidentCounts ??
                        []
                      ).map((pac) =>
                        pac.year === ac.year
                          ? { year: ac.year, count: newValue }
                          : pac
                      ),
                    }))
                  }}
                />
                <Button
                  importance={'secondary'}
                  text={localization.text(
                    'remove-accident-count',
                    'Verwijder jaar'
                  )}
                  onClick={() => {
                    setCompanyChange((previous) => ({
                      ...previous,
                      accidentCounts: (
                        previous.accidentCounts ?? company.accidentCounts
                      ).filter((b) => b.year !== ac.year),
                    }))
                  }}
                />
              </div>
            ))}
            <div className="add-accident-count">
              <NumberInput
                value={accidentCountYearToAdd}
                onChange={(newValue) => {
                  setAccidentCountYearToAdd(newValue)
                }}
                max={accidentMostRecentYear}
                min={1900}
              />
              <Button
                importance={'secondary'}
                text={localization.text('add-accident-count', 'Voeg jaar toe')}
                onClick={() => {
                  setCompanyChange((previous) => ({
                    ...previous,
                    accidentCounts: [
                      ...(previous.accidentCounts ??
                        company.accidentCounts ??
                        []),
                      { year: accidentCountYearToAdd, count: 0 },
                    ],
                  }))
                  setAccidentCountYearToAdd((previous) => previous - 1)
                }}
                disabled={(company.accidentCounts ?? []).some(
                  (ac) => ac.year === accidentCountYearToAdd
                )}
              />
            </div>
          </div>

          <div className="branches">
            <div className="label">
              {localization.text('branches', 'Vestigingen')}
            </div>
            {(company.branches ?? []).map((branch) => (
              <div key={branch.number} className="int-branch-option">
                <div className="branch-name">
                  {formatCompanyBranch(branch, countryLocalization)}
                </div>
                {!isBelgianVatNumber && (
                  <Button
                    importance={'secondary'}
                    text={localization.text(
                      'remove-branch',
                      'Verwijder vestiging'
                    )}
                    size="small"
                    onClick={() => {
                      setCompanyChange((previous) => ({
                        ...previous,
                        branches: (
                          previous.branches ??
                          company.branches ??
                          []
                        ).filter((b) => b !== branch),
                      }))
                    }}
                  />
                )}
              </div>
            ))}
            {!isBelgianVatNumber && (
              <div className="add-branch">
                <h3>{localization.text('add-branch', 'Voeg vestiging toe')}</h3>
                <TextInput
                  label={localization.text('branch-number', 'Vestigingsnummer')}
                  isReadOnly={saveInProgress}
                  onChange={(newValue) => {
                    setBranchToAdd((p) => ({ ...p, number: newValue }))
                  }}
                  value={branchToAdd.number}
                  helperText={
                    branchValidationErrors.find(
                      (ve) => ve.property === 'branch-number'
                    )?.type === 'isRequired'
                      ? localization.text(
                          'branch-number-is-required',
                          'Vestigingsnummer is verplicht'
                        )
                      : undefined
                  }
                />
                <TextInput
                  label={localization.text(
                    'branch-addressLine1',
                    'Straat en huisnummer'
                  )}
                  isReadOnly={saveInProgress}
                  onChange={(newValue) => {
                    setBranchToAdd((p) => ({
                      ...p,
                      addressLine1: newValue,
                    }))
                  }}
                  value={branchToAdd.addressLine1}
                />
                <TextInput
                  label={localization.text('branch-postalCode', 'Postcode')}
                  isReadOnly={saveInProgress}
                  onChange={(newValue) => {
                    setBranchToAdd((p) => ({ ...p, postalCode: newValue }))
                  }}
                  value={branchToAdd.postalCode}
                />
                <TextInput
                  label={localization.text('branch-city', 'Gemeente')}
                  isReadOnly={saveInProgress}
                  onChange={(newValue) => {
                    setBranchToAdd((p) => ({ ...p, city: newValue }))
                  }}
                  value={branchToAdd.city}
                />

                <SingleSelect
                  label={localization.text('branch-country', 'Land')}
                  items={CountryCodes.map((code) => ({
                    value: code,
                    label: code ? countryLocalization.text(code, code) : '',
                  }))}
                  isReadOnly={saveInProgress}
                  onChange={(newValue) => {
                    setBranchToAdd((previous) => ({
                      ...previous,
                      countryCode: newValue,
                    }))
                  }}
                  value={branchToAdd.countryCode}
                  isValid={
                    !branchValidationErrors.some(
                      (ve) => ve.property === 'branch-countryCode'
                    )
                  }
                  helperText={
                    branchValidationErrors.find(
                      (ve) => ve.property === 'branch-countryCode'
                    )?.type === 'isRequired'
                      ? localization.text(
                          'branch-country-code-missing',
                          'Kies een geldig land'
                        )
                      : undefined
                  }
                />
                {branchValidationErrors.length === 0 && !saveInProgress ? (
                  <Button
                    importance={'primary'}
                    text={localization.text('add-branch', 'Voeg vestiging toe')}
                    onClick={() => {
                      setCompanyChange((previous) => ({
                        ...previous,
                        branches: [
                          ...(
                            previous.branches ??
                            company.branches ??
                            []
                          ).filter((b) => b !== branchToAdd),
                          branchToAdd,
                        ],
                      }))
                      setBranchToAdd({
                        ...emptyBranchToAdd,
                        countryCode: branchToAdd.countryCode,
                      })
                    }}
                  />
                ) : null}
              </div>
            )}
          </div>
          {company.lastKpbCheck != null && (
            <div
              className={`display-field ${
                company.lastKpbCheckSuccess === true
                  ? 'kbo-success'
                  : 'kbo-fail'
              }`}
            >
              <div className="display-label">
                {localization.text('lastKpbCheck', 'Laatste Kbp check')}
              </div>
              <div className="display-value">
                {company.lastKpbCheckSuccess === true
                  ? `${localization.text(
                      'kbo-success',
                      'success'
                    )} ${toBelgianDate(company.lastKpbUpdate)}`
                  : company.lastKpbUpdate != null
                  ? `${localization.text(
                      'kbo-failure-since',
                      'failure since'
                    )} ${toBelgianDate(company.lastKpbUpdate)}`
                  : localization.text('kbo-failure', 'failure')}
                {company.lastKpbCheckSuccess !== true && (
                  <a
                    href={getKboUrl(company.vatNumber)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {localization.text('link-kbo', 'Link KBO / BCA')}
                  </a>
                )}
              </div>
            </div>
          )}

          <div className="buttons">
            <Button
              importance={'primary'}
              text={localization.text(
                'safe-edit-button',
                'Pas onderneming aan'
              )}
              onClick={saveCompany}
              disabled={validationErrors.length > 0 || saveInProgress}
            />
          </div>
        </>
      )}
    </div>
  )
}

interface validationError {
  property: string
  type: 'isRequired' | 'format'
}
